<template>
  <div class="knowledge-base">
    <el-card class="kb-container">
      <!-- 搜索和操作区域 -->
      <div class="toolbar">
        <!-- 只有负责人能上传。它读的是登录时写入的 localStorage.userInfo，新授予的负责人
             不重登会暂时看不到；服务端 requireKnowledgeOwner 才是真闸门 -->
        <el-button v-if="isKnowledgeOwner" type="primary" @click="openUploadDialog">
          <el-icon><UploadFilled /></el-icon>上传文档
        </el-button>

        <div class="search-area">
          <el-input
            v-model="searchParams.keyword"
            placeholder="搜索文件名或正文关键词"
            clearable
            style="width: 260px"
            @keyup.enter="handleSearch"
            @clear="handleSearch"
          />
          <el-button @click="handleSearch">搜索</el-button>
        </div>
      </div>

      <!-- 按文件类型筛选：切 tab 回到第 1 页，关键词沿用已提交的那个 -->
      <el-tabs v-model="category" class="kb-category-tabs" @tab-change="handleCategoryChange">
        <el-tab-pane
          v-for="tab in categoryTabs"
          :key="tab.value"
          :label="tab.label"
          :name="tab.value"
        />
      </el-tabs>

      <!-- 文档列表表格 -->
      <el-table v-loading="loading" :data="docList" border style="width: 100%">
        <el-table-column label="文件名" min-width="200">
          <template #default="scope">
            <el-link v-if="canPreview(scope.row)" type="primary" @click="openPreview(scope.row)">
              {{ scope.row.name }}{{ scope.row.ext }}
            </el-link>
            <span v-else>{{ scope.row.name }}{{ scope.row.ext }}</span>
          </template>
        </el-table-column>
        <!-- 点片段打开预览并定位到命中处；没有片段时不做成可点的，免得点了什么都不发生 -->
        <el-table-column label="片段" min-width="240">
          <template #default="scope">
            <span
              v-if="scope.row.snippet && canPreview(scope.row)"
              class="kb-snippet-link"
              v-html="highlightSnippet(scope.row.snippet, searchedKeyword)"
              @click="openPreview(scope.row)"
            ></span>
            <span v-else v-html="highlightSnippet(scope.row.snippet, searchedKeyword)"></span>
          </template>
        </el-table-column>
        <el-table-column label="大小" width="100" align="center">
          <template #default="scope">{{ formatSize(scope.row.size) }}</template>
        </el-table-column>
        <el-table-column prop="uploader_name" label="上传人" width="120" align="center" />
        <el-table-column label="上传时间" width="180" align="center">
          <template #default="scope">{{ formatDateTime(scope.row.created_at) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="220" fixed="right" align="center">
          <template #default="scope">
            <el-button size="small" @click="downloadDoc(scope.row)">下载</el-button>
            <!-- 改名/删除只认后端逐行回填的 can_manage，不认本地身份 -->
            <el-button v-if="canManageRow(scope.row)" size="small" @click="renameDoc(scope.row)">
              改名
            </el-button>
            <el-button
              v-if="canManageRow(scope.row)"
              size="small"
              type="danger"
              @click="deleteDoc(scope.row)"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination">
        <el-pagination
          v-model:current-page="pagination.current"
          :total="pagination.total"
          :page-size="pagination.size"
          background
          layout="total, prev, pager, next"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <!-- 预览弹窗 -->
    <KnowledgeDocPreview
      v-model:visible="previewVisible"
      :doc="previewDoc"
      :keyword="searchedKeyword"
      @download="downloadDoc"
    />

    <!-- 上传弹窗 -->
    <el-dialog v-model="uploadDialogVisible" title="上传文档" width="500px" :close-on-click-modal="false">
      <el-upload
        v-model:file-list="uploadFileList"
        :auto-upload="false"
        multiple
        drag
        :accept="allowedAccept"
        :on-change="handleUploadFileChange"
        :on-remove="handleUploadFileRemove"
      >
        <div class="upload-drag-area">
          <el-icon :size="28" color="#909399"><UploadFilled /></el-icon>
          <div class="upload-text">将文件拖到此处，或<em>点击上传</em></div>
        </div>
        <template #tip>
          <div class="upload-tip">{{ uploadTip }}</div>
        </template>
      </el-upload>
      <div v-if="uploading" class="upload-tip">正在上传第 {{ uploadCurrent }}/{{ uploadTotal }} 个文件</div>
      <el-alert
        v-if="uploadErrors.length > 0"
        type="error"
        :closable="false"
        title="以下文件上传失败，可移除后重试或直接再次上传"
        style="margin-top: 12px"
      >
        <div v-for="item in uploadErrors" :key="item.name">{{ item.name }}：{{ item.error }}</div>
      </el-alert>
      <template #footer>
        <el-button @click="uploadDialogVisible = false" :disabled="uploading">取消</el-button>
        <el-button type="primary" :loading="uploading" @click="submitUpload">确定上传</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { UploadFilled } from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox } from "element-plus";
import { formatDateTime } from "@/utils/date";
import { highlightSnippet } from "@/utils/highlight";
import {
  withTimeout,
  uploadFilesSequentially,
  isDialogDismiss,
  errorText,
  triggerBrowserDownload,
  stripDuplicateExt,
  knowledgeFileError,
  KNOWLEDGE_ALLOWED_EXTS,
  KNOWLEDGE_MAX_SIZE,
  KNOWLEDGE_MAX_IMAGE_SIZE,
  canManageRow,
  KNOWLEDGE_CATEGORY_TABS,
} from "@/utils/knowledgeBase";
import {
  listKnowledgeDocs,
  uploadKnowledgeDoc,
  renameKnowledgeDoc,
  deleteKnowledgeDoc,
  knowledgeDocDownloadUrl,
} from "@/api/report-system/knowledge-base";
import KnowledgeDocPreview from "@/components/KnowledgeDocPreview.vue";

const LIST_TIMEOUT_MS = 30000; // 列表请求兜底超时：拦截器对 HTTP 错误不 resolve，避免 loading 永远转

export default {
  name: "KnowledgeBase",
  components: {
    UploadFilled,
    KnowledgeDocPreview,
  },
  data() {
    return {
      searchParams: {
        keyword: "",
      },
      searchedKeyword: "", // 已提交且 trim 过的关键词：查询与高亮都用它，不随输入框实时变化
      pagination: {
        current: 1,
        size: 10,
        total: 0,
      },
      docList: [],
      loading: false,
      uploadDialogVisible: false,
      uploadFileList: [],
      uploading: false,
      uploadCurrent: 0,
      uploadTotal: 0,
      uploadErrors: [],
      allowedAccept: KNOWLEDGE_ALLOWED_EXTS.map((e) => `.${e}`).join(","),
      uploadTip: `支持 ${KNOWLEDGE_ALLOWED_EXTS.join("、")}；单个文件不超过 ${
        KNOWLEDGE_MAX_SIZE / 1024 / 1024
      }MB，图片不超过 ${KNOWLEDGE_MAX_IMAGE_SIZE / 1024 / 1024}MB`,
      category: "",
      fetchSeq: 0,
      categoryTabs: KNOWLEDGE_CATEGORY_TABS,
      previewVisible: false,
      previewDoc: null,
    };
  },
  computed: {
    isKnowledgeOwner() {
      try {
        const info = JSON.parse(localStorage.getItem("userInfo") || "{}");
        return info.is_knowledge_owner === true || info.user_type === 1;
      } catch (e) {
        return false;
      }
    },
  },
  methods: {
    canManageRow,
    // mp4 不支持在线预览（没有定位需求，也没有能跳转的版式），只给下载
    canPreview(row) {
      return (row.ext || "").toLowerCase() !== ".mp4";
    },
    openPreview(row) {
      this.previewDoc = row;
      this.previewVisible = true;
    },
    formatDateTime(t) {
      return formatDateTime(t);
    },
    highlightSnippet(text, keyword) {
      return highlightSnippet(text, keyword);
    },
    formatSize(size) {
      if (!size) return "0 B";
      if (size < 1024) return size + " B";
      if (size < 1024 * 1024) return (size / 1024).toFixed(1) + " KB";
      return (size / (1024 * 1024)).toFixed(1) + " MB";
    },
    // 搜索
    handleSearch() {
      this.searchedKeyword = this.searchParams.keyword.trim();
      this.pagination.current = 1;
      this.fetchDocs();
    },
    handleCategoryChange() {
      this.pagination.current = 1;
      this.fetchDocs();
    },
    // 分页切换
    handleCurrentChange(current) {
      this.pagination.current = current;
      this.fetchDocs();
    },
    // 获取文档列表
    async fetchDocs() {
      // 快速连切 tab / 翻页时，只认最后一次请求的结果，免得慢的旧响应盖掉新 tab
      const seq = ++this.fetchSeq;
      this.loading = true;
      try {
        const params = {
          keyword: this.searchedKeyword,
          page: this.pagination.current,
          page_size: this.pagination.size,
        };
        if (this.category) params.category = this.category;
        const response = await withTimeout(listKnowledgeDocs(params), LIST_TIMEOUT_MS, "请求超时");
        if (seq !== this.fetchSeq) return;
        this.docList = response.data.rows || [];
        this.pagination.total = response.data.total || 0;
      } catch (error) {
        if (seq !== this.fetchSeq) return;
        const text = errorText(error);
        if (text !== null) {
          ElMessage.error("获取知识库列表失败: " + text);
        }
      } finally {
        if (seq === this.fetchSeq) this.loading = false;
      }
    },
    // 打开上传弹窗
    openUploadDialog() {
      this.uploadFileList = [];
      this.uploading = false;
      this.uploadErrors = [];
      this.uploadDialogVisible = true;
    },
    // 前端预校验：判据在 utils/knowledgeBase.js 里（可单测），这里只负责弹提示
    validateFile(file) {
      const err = knowledgeFileError(file);
      if (err) {
        ElMessage.error(`${file.name}：${err}`);
        return false;
      }
      return true;
    },
    handleUploadFileChange(file) {
      if (!this.validateFile(file)) {
        const idx = this.uploadFileList.indexOf(file);
        if (idx > -1) this.uploadFileList.splice(idx, 1);
      }
    },
    handleUploadFileRemove(file) {
      const idx = this.uploadFileList.indexOf(file);
      if (idx > -1) this.uploadFileList.splice(idx, 1);
    },
    // 提交上传：逐个文件顺序上传（每个请求一个文件），每个文件单独显示进度与成功/失败
    async submitUpload() {
      if (this.uploadFileList.length === 0) {
        ElMessage.warning("请先选择要上传的文件");
        return;
      }
      const pending = this.uploadFileList.filter((f) => f.status !== "success");
      if (pending.length === 0) {
        this.uploadDialogVisible = false;
        return;
      }
      this.uploading = true;
      this.uploadErrors = [];
      this.uploadCurrent = 0;
      this.uploadTotal = pending.length;
      let results = [];
      try {
        results = await uploadFilesSequentially(this.uploadFileList, (file, onProgress) => {
          this.uploadCurrent += 1;
          return uploadKnowledgeDoc(file.raw || file, onProgress);
        });
      } finally {
        this.uploading = false;
      }
      const failed = results.filter((r) => !r.ok);
      const succeeded = results.length - failed.length;
      if (failed.length === 0) {
        ElMessage.success(`上传成功 ${succeeded} 个文件`);
        this.uploadDialogVisible = false;
      } else {
        // 有失败时保留弹窗，逐个列出失败原因；再次提交只会上传未成功的文件
        this.uploadErrors = failed;
        ElMessage.error(`${failed.length} 个文件上传失败，成功 ${succeeded} 个`);
      }
      if (succeeded > 0) {
        this.handleSearch();
      }
    },
    // 下载：交给浏览器原生下载，点击即出现在浏览器下载栏并显示进度
    downloadDoc(row) {
      triggerBrowserDownload(knowledgeDocDownloadUrl(row.id), `${row.name}${row.ext}`);
    },
    // 改名
    async renameDoc(row) {
      try {
        const { value } = await ElMessageBox.prompt(`请输入新名称（扩展名 ${row.ext} 不变）`, "改名", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          inputValue: row.name,
        });
        await renameKnowledgeDoc(row.id, stripDuplicateExt(value, row.ext));
        ElMessage.success("改名成功");
        this.fetchDocs();
      } catch (error) {
        const text = isDialogDismiss(error) ? null : errorText(error);
        if (text !== null) {
          ElMessage.error("改名失败: " + text);
        }
      }
    },
    // 删除（二次确认）
    async deleteDoc(row) {
      try {
        await this.$confirm(`确定要删除「${row.name}${row.ext}」吗？`, "提示", {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        });
        await deleteKnowledgeDoc(row.id);
        ElMessage.success("删除成功");
        this.fetchDocs();
      } catch (error) {
        const text = isDialogDismiss(error) ? null : errorText(error);
        if (text !== null) {
          ElMessage.error("删除失败: " + text);
        }
      }
    },
  },
  mounted() {
    this.fetchDocs();
  },
};
</script>

<style scoped>
.knowledge-base {
  padding: 20px;
}

.kb-container {
  max-width: 1200px;
  margin: 0 auto;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.search-area {
  display: flex;
  align-items: center;
  gap: 10px;
}

.kb-snippet-link {
  cursor: pointer;
  color: #409eff;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.upload-drag-area {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px 0;
}

.upload-tip {
  color: #909399;
  font-size: 12px;
}

:deep(mark) {
  background-color: #ffe58f;
  color: inherit;
  padding: 0 2px;
}
</style>
